/**
 * One `session.started` per browser tab session (R10 step 1): the device class and browser
 * family, so the Usage screen can say whether the phone matters. Coarse on purpose: three device
 * classes, a handful of browser names, the viewport width rounded to the hundred. Never a full
 * user agent string.
 */
import { track } from './events';

const STARTED_KEY = 'paige.session.started';

export type DeviceClass = 'phone' | 'tablet' | 'desktop';

export function deviceClass(width: number, touch: boolean): DeviceClass {
  if (width < 768) return 'phone';
  if (width < 1024 && touch) return 'tablet';
  return 'desktop';
}

/** The browser's family from its user agent; 'other' rather than a guess. */
export function browserFamily(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('firefox/')) return 'firefox';
  if (ua.includes('chrome/') || ua.includes('crios/')) return 'chrome';
  if (ua.includes('safari/')) return 'safari';
  return 'other';
}

/** Records the session's start once; a reload in the same tab does not count again. */
export function trackSessionStart(win: Window = window): void {
  try {
    if (sessionStorage.getItem(STARTED_KEY)) return;
    sessionStorage.setItem(STARTED_KEY, '1');
  } catch {
    /* no storage: still worth one event */
  }
  const width = win.innerWidth || 0;
  const touch = win.navigator.maxTouchPoints > 0;
  track('session.started', {
    feature: 'session.started',
    detail: {
      device: deviceClass(width, touch),
      browser: browserFamily(win.navigator.userAgent),
      width: Math.round(width / 100) * 100,
    },
  });
}
